import { yupResolver } from '@hookform/resolvers/yup';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { MoonLoader } from 'react-spinners';
import styled from 'styled-components';
import * as Yup from 'yup';
import { PromiseLoader, Input, UseAutocomplete, FontTheme } from "react-builder";
import { AuthContext, ISupportRequestResponse } from "react-builder";
import { Attribute, AttributeGroup, AttributeProperty, ComponentProps, ButtonStateAttributes } from "react-builder";
import ReCAPTCHA from "react-google-recaptcha";
import { ProductServerAuthContext } from '../providers/ProductServerAuth';

interface ISupportFormData {
  email: string,
  fullName: string,
  mobileCode: string,
  type: string,
  mobileNumber: string,
  digits: string,
  countryCode: string
  captcha: string
}

export const SupportFormTheme = styled.div((props: ComponentProps<SupportFormAttributes>) => `

  & {
    .term-wrap label {
        display: -webkit-box;
        display: -moz-box;
        display: -ms-flexbox;
        display: -webkit-flex;
        display: flex;
    }

    input {
        display: block;
    }

    input[type="checkbox"] {
        margin: 1px 5px 0 0;
    }

    .MuiAutocomplete-popper{
      width: auto !important;
    }
    .MuiInputBase-root{
      padding: 0 !important;
      margin : 0;
    }

    input[name="mobileNumber"]{
      width: 100%;
    }

    .form-group.mobileNumber{
      flex-grow: 1;
    }
  }

  & form button,
  .signin form button,
  #support form button {
      width: 100%;
  }

  form .input-error {
      margin: 0;
      padding: 0;
      font-size: 12px;
      font-weight: 400;
      color: #cc0700;
  }

  .auto{
    z-index: 100;
  }

  .mobile{
    ul{
      width: auto !important;
      z-index: 100;
    }
  }

  ${props.attributes.toCSS()}

`);

@AttributeGroup({
  title: 'Support Form',
  blockName: 'SupportForm',
})
export class SupportFormAttributes extends Attribute {
  @AttributeProperty({ type: 'String' })
  emailLabel: string = "Email";
  @AttributeProperty({ type: 'String' })
  nameLabel: string = "Name";
  @AttributeProperty({ type: 'String' })
  mobileNumberLabel: string = "Mobile";
  @AttributeProperty({ type: 'String' })
  digitsLabel: string = "Last four digits";
  @AttributeProperty({ type: 'String' })
  redirectUrl: string = '';
  @AttributeProperty({ type: 'String' })
  captchaSiteKey: string = '6LdYwk0UAAAAAJ21QqEgn8cT563RdQ-jUsV4pakZ';

  @AttributeProperty({ type: 'Object', cssTarget: ' .success-message' })
  successMessage: FontTheme = new FontTheme().fromJSON({
    color: 'green'
  });

  @AttributeProperty({ type: 'Object', cssTarget: ' button[type="submit"].support' })
  button: ButtonStateAttributes = new ButtonStateAttributes()
}

const SupportForm = (props: ComponentProps<SupportFormAttributes>) => {

  const { isLoggedIn, isLoginDialogOpen, setLoginDialogOpen, signup, supportRequestError, sendSupportRequest } = useContext(AuthContext)!;
  const authV2 = useContext(ProductServerAuthContext);
  // route params
  const routeParams = useParams<{ type?: string }>();
  const formSchema = Yup.object().shape({
    email: Yup.string()
      .required('Email is required')
      .matches(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/, "Valid email address required"),
    fullName: Yup.string()
      .required('Full name is required')
      .matches(/[a-z0-9._%+-]+ [a-z0-9.-]*$/, "Valid full name required"),
    mobileCode: Yup.string()
      .required('Mobile code is mendatory'),
    mobileNumber: Yup.string()
      .required('Mobile number is mendatory')
      .matches(/^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/, "Valid mobile number required"),
    type: Yup.string()
      .required('Required'),
    digits: Yup.string()
      .max(4)
      .min(4)
      .required('Last four digits are required'),
    captcha: Yup.string()
      .required('Captcha is required')
  })
  const formOptions = { resolver: yupResolver(formSchema) }
  const { register, watch, formState, handleSubmit, setValue } = useForm<ISupportFormData>(formOptions);

  const [supportRequestPromise, setSupportRequestPromise] = useState<Promise<any> | undefined>(undefined);

  const onSubmit: SubmitHandler<ISupportFormData> = (data: ISupportFormData) => {
    console.log(data, formState.errors.email);
    setSupportRequestPromise((authV2.sendSupportRequest ?? sendSupportRequest)({
      ccFourDigits: data.digits,
      countryCode: data.countryCode,
      email: data.email,
      fullName: data.fullName,
      mobileNumber: data.mobileCode.replace("+", "") + data.mobileNumber,
      type: data.type,
      captcha: data.captcha,
    }).then((success) => {
      console.log(success);
      
      if (props.attributes.redirectUrl)
        window.location.href = props.attributes.redirectUrl;
      return success;
    }))
  }

  // check type router parameter and update
  React.useEffect(() => {
    if (parseInt(routeParams.type ?? '') >= 0) {
      setValue('type', routeParams.type!, { shouldValidate: true });
    }
  }, [routeParams.type]);

  useEffect(() => {

    fetch(`https://storage.googleapis.com/ngx-packages/country-states.json?v=${Math.ceil(new Date().getTime() / 1000 / 60 / 60 / 24)}`).then((list) => {
      return list.json();
    }).then((list) => {
      setCountries(list.map((i: { code2: string, name: string, number: string }) => ({ mobileCode: i.number, countryCode: i.code2, countryName: i.name })))
    })
  }, []);

  const passInput = useRef<HTMLInputElement>();

  const [countries, setCountries] = useState<{ mobileCode: string, countryCode: string, countryName: string }[]>([]);

  return (
    <SupportFormTheme {...props} >
      <PromiseLoader<ISupportRequestResponse>
        promise={supportRequestPromise}
        overlayColor={'rgba(247, 247, 247,.6)'}
        loader={<MoonLoader loading={true} size={60} />
        }
      >
        {res => res?.success && res?.message ? <div><p className='success-message'>{res.message}</p></div> : <div className="form">
          <div ng-show="!registrationOk" className="input-error ng-binding ng-hide"></div>
          <form ng-show="showForm" className="ng-pristine ng-valid" onSubmit={handleSubmit(onSubmit)} noValidate>

            <Input name='fullName' type='text' label={props.attributes.nameLabel} register={register} error={formState.errors.fullName} />
            <Input name='email' type='email' label={props.attributes.emailLabel} register={register} error={formState.errors.email} />
            <div style={{ display: 'flex', alignItems: 'stretch', gap: '10px' }}>
              <div className='form-group mobile'>
                <UseAutocomplete
                  options={countries}
                  renderOption={(option, props) => <>{option.countryName} +{option.mobileCode}</>}
                  getOptionLabel={(option) => `+${option.mobileCode}`}
                  label={'+'}
                  onChange={(event: any, newValue: any) => {
                    // register.mobileImage
                    setValue('mobileCode', newValue.mobileCode, { shouldValidate: true });
                    setValue('countryCode', newValue.countryCode);
                  }}
                  filterOptions={(options, state) => {
                    return options.filter((o) => (o.countryName + " " + o.mobileCode).toLowerCase().indexOf(state.inputValue.toLowerCase()) > -1);
                  }}
                ></UseAutocomplete>
                {/* <Autocomplete
                    disablePortal
                    style={{
                      padding: 0,
                      margin: 0,

                    }}
                    id="combo-box-demo"
                    options={[{ mobileCode: "972", countryCode: "IL", countryName: "Israel" }, { mobileCode: "1", countryCode: "US", countryName: 'United States' }]}
                    sx={{ width: 100 }}
                    componentsProps={{
                      paper: {
                        sx: {
                          width: 200,
                          left: 0
                          
                          
                        },
                        
                      }
                    }}
                    // size="small"
                    disableClearable={true}
                    onChange={(event: any, newValue: any) => {
                      // register.mobileImage
                      setValue('mobileCode', newValue.mobileCode, { shouldValidate: true });
                      setValue('countryCode', newValue.countryCode);
                    }}
                    renderInput={(params) => <TextField style={{ width: '100%' }} {...params} placeholder="+" />}
                    getOptionLabel={(option) => `+${option.mobileCode}`}
                    renderOption={(props, option) => <li {...props}>{option.countryName} +{option.mobileCode}</li>}
                  /> */}
                {formState.errors.mobileCode ? <p className='input-error'>Required</p> : <></>}
              </div>
              <Input name='mobileNumber' type='tel' label={props.attributes.mobileNumberLabel} register={register} error={formState.errors.mobileNumber} />

            </div>

            <div className="form-group" >
              <select {...register("type")} >
                <option value="" selected disabled >Select a subject</option>
                <option value="0">Cancel Membership</option>
                <option value="4">Forgot Password</option>
                <option value="5">Refund</option>
              </select>
              <p className='input-error'>{formState.errors?.type?.message}</p>
            </div>

            <Input name='digits' maxLength={4} type='text' label={props.attributes.digitsLabel} register={register} error={formState.errors.digits} />

            <ReCAPTCHA
              sitekey={props.attributes.captchaSiteKey}
              onChange={(e) => {
                // console.log(e);
                setValue('captcha', e ?? '')
              }}
            />
            <p className='input-error' >{formState.errors.captcha?.message}</p>

            <button type="submit" className='support' >SEND</button>
            {(authV2.supportRequestError ?? supportRequestError) && <p className='input-error' >{authV2.supportRequestError ?? supportRequestError}</p>}
          </form>
        </div>}
      </PromiseLoader>
    </SupportFormTheme>
  );

};

SupportForm.prototype.Props = SupportFormAttributes;

export default SupportForm;